/**
 * Static metadata map: variant name → Jeep configurator metadata.
 *
 * The Vivid API provides { id, name, enabled, image } per variant but does NOT
 * carry the PlayCanvas asset names, colour hex codes, or the legacy Threekit
 * assetIds needed by the existing UI components. This map bridges that gap.
 *
 * Structure:  ATTRIBUTE_METADATA[attributeName][variantName] → JeepVariantMetadata
 *
 * When a new variant is added in the Vivid CMS its `name` must match a key
 * here, otherwise it will be given sensible defaults (slug-based assetId, etc.).
 */

import type { JeepVariantMetadata } from './types';

// ── Jeep Type ──
const JEEP_TYPE: Record<string, JeepVariantMetadata> = {
  '2 Door': { assetId: '2d', pcOption: '2d', pcAsset: '2DJeep_Parts', extra: { option: '2d', shadowScale: 7 } },
  '4 Door': { assetId: '4d', pcOption: '4d', pcAsset: '4DJeep_Parts', extra: { option: '4d', shadowScale: 8.6 } },
};

// ── Paint color ──
const PAINT_COLOR: Record<string, JeepVariantMetadata> = {
  Green:  { assetId: 'green-paint',  pcOption: 'Green',  pcAsset: null, priceId: 'ext-color-0', extra: { _bg: '#2D5016', option: 'Green' } },
  Red:    { assetId: 'red-paint',    pcOption: 'Red',    pcAsset: null, priceId: 'ext-color-1', extra: { _bg: '#8B1A1A', option: 'Red' } },
  Black:  { assetId: 'black-paint',  pcOption: 'Black',  pcAsset: null, priceId: 'ext-color-2', extra: { _bg: '#000000', option: 'Black' } },
  Yellow: { assetId: 'orange-paint', pcOption: 'Orange', pcAsset: null, priceId: 'ext-color-3', extra: { _bg: '#FFD700', option: 'Orange' } },
  White:  { assetId: 'white-paint',  pcOption: 'White',  pcAsset: null, priceId: 'ext-color-4', extra: { _bg: '#FFFFFF', option: 'White' } },
  Grey:   { assetId: 'grey-paint',   pcOption: 'Grey',   pcAsset: null, priceId: 'ext-color-5', extra: { _bg: '#808080', option: 'Grey' } },
};

// ── Wheels ──
const WHEELS: Record<string, JeepVariantMetadata> = {
  Default:                { assetId: 'default-rim',          pcOption: 'default',             pcAsset: 'Default_Rim_FL',              priceId: '14426' },
  'WHL Quad Hardroc':     { assetId: 'whl-quad-hardroc',     pcOption: 'whl-quad-hardroc',    pcAsset: 'WHL_Quad_Hardroc_Rim_FL',     priceId: '14427' },
  'ZHE Quad CJ Retro AR': { assetId: 'zhe-quad-cj-retro-ar', pcOption: 'zhe-quad-cj-retro-ar', pcAsset: 'ZHE_Quad_CJ_Retro_AR_Rim_FL', priceId: '22000' },
};

// ── Tire ──
const TIRE: Record<string, JeepVariantMetadata> = {
  Default:  { assetId: 'default-tire',  pcOption: 'default',  pcAsset: 'Default_Tire_FL',  priceId: '20469' },
  Wrangler: { assetId: 'wrangler-tire', pcOption: 'wrangler', pcAsset: 'Wrangler_Tire_FL', priceId: '20470' },
};

// ── Front Bumper ──
const FRONT_BUMPER: Record<string, JeepVariantMetadata> = {
  Default:        { assetId: 'default-front-bumper', pcOption: 'default',  pcAsset: 'Default_FrontBumper',         priceId: '13763' },
  Carnivore:      { assetId: 'carnivore-bumper',     pcOption: 'carnivore', pcAsset: 'Carnivore_FrontBumper',       priceId: '15038' },
  'RAA Quad Alum': { assetId: 'raa-quad-bumper',      pcOption: 'raa-quad',  pcAsset: 'FrontBumper_RAA_Quad_Alum',  priceId: '23556' },
};

// ── Rear Bumper ──
const REAR_BUMPER: Record<string, JeepVariantMetadata> = {
  Default:        { assetId: 'default-rear-bumper',  pcOption: 'default',      pcAsset: 'Default_RearBumper',       priceId: '13765' },
  'RAA Quad Alum': { assetId: 'raa-quad-rear',        pcOption: 'raa-quad',     pcAsset: 'RAA_Quad_Alum_RearBumper', priceId: '21996' },
  'Rugged Ridge':  { assetId: 'rugged-ridge-rear',    pcOption: 'rugged-ridge', pcAsset: 'Rugged_Ridge_RearBumper',  priceId: '26000' },
};

// ── Front Fender ──
const FRONT_FENDER: Record<string, JeepVariantMetadata> = {
  Default:              { assetId: 'default-fender',      pcOption: 'default',           pcAsset: 'Defaut_Fender_FL',            priceId: 'fender-def' },
  'Quadratec Baja HD':  { assetId: 'quadratec-baja-hd',   pcOption: 'quadratec-baja-hd', pcAsset: 'Quadratec_Baja_HD_Fender_FL', priceId: '26030' },
  'Rugged Ridge Max':   { assetId: 'rugged-ridge-max',    pcOption: 'rugged-ridge-max',  pcAsset: 'Rugged_Ridge_Max_Fender_FL',  priceId: '25274' },
};

// ── Rear Fender ──
const REAR_FENDER: Record<string, JeepVariantMetadata> = {
  Default:              { assetId: 'default-rear-fender',     pcOption: 'default',           pcAsset: 'Defaut_Fender_RL',            priceId: 'rfender-def' },
  'Quadratec Baja HD':  { assetId: 'quadratec-baja-hd-rear',  pcOption: 'quadratec-baja-hd', pcAsset: 'Quadratec_Baja_HD_Fender_RL', priceId: '26031' },
  'Rugged Ridge Max':   { assetId: 'rugged-ridge-max-rear',   pcOption: 'rugged-ridge-max',  pcAsset: 'Rugged_Ridge_Max_Fender_RL',  priceId: '25275' },
};

// ── Mirror ──
const MIRROR: Record<string, JeepVariantMetadata> = {
  Default:                      { assetId: 'default-mirror',    pcOption: 'default',            pcAsset: 'Default_Mirror',                  priceId: 'mirror-def' },
  'QR Mirror Rectangle':       { assetId: 'qr-rectangle',      pcOption: 'qr-rectangle',       pcAsset: 'Quick_Release_Mirror_Rectangle',  priceId: 'mirror-rect' },
  'QR Mirror Round':           { assetId: 'qr-round',          pcOption: 'qr-round',           pcAsset: 'Quick_Release_Mirror_Round',      priceId: 'mirror-round' },
  'Quadratec Factory Styling': { assetId: 'quadratec-factory',  pcOption: 'quadratec-factory',  pcAsset: 'Quadratec_Factory_Styling_Mirror', priceId: 'mirror-factory' },
};

// ── Hood Catcher ──
const HOOD_CATCHER: Record<string, JeepVariantMetadata> = {
  Default:        { assetId: 'default-hoodcatcher', pcOption: 'default',       pcAsset: 'Default_HoodCatcher',    priceId: 'hc-def' },
  'HGO Quad':     { assetId: 'hgo-quad',            pcOption: 'hgo-quad',      pcAsset: 'HGO_Quad_HoodCatcher',   priceId: 'hc-hgo' },
  'Rugged Ridge': { assetId: 'rugged-ridge-hc',     pcOption: 'rugged-ridge',  pcAsset: 'Rugged_Ridge_HoodCatcher', priceId: 'hc-rr' },
};

// ── Light Bar ──
const LIGHT_BAR: Record<string, JeepVariantMetadata> = {
  None:        { assetId: 'none-lightbar', pcOption: 'none',      pcAsset: null,        priceId: 'lb-none' },
  'Light Bar': { assetId: 'light-bar',     pcOption: 'light-bar', pcAsset: 'Light_Bar', priceId: 'lb-bar' },
};

// ── Side Steps ──
const SIDE_STEPS: Record<string, JeepVariantMetadata> = {
  None:              { assetId: 'none-sidestep',   pcOption: 'none',            pcAsset: null,                     priceId: 'ss-none' },
  Carnivore:         { assetId: 'carnivore-ss',    pcOption: 'carnivore',       pcAsset: 'Carnivore_SideStep_4D',  priceId: 'ss-carnivore' },
  'Quadratec QR3':   { assetId: 'quadratec-qr3',   pcOption: 'quadratec-qr3',  pcAsset: 'Quadratec_QR3_SideStep_4D', priceId: 'ss-qr3' },
  'Quadratec QR4':   { assetId: 'quadratec-qr4',   pcOption: 'quadratec-qr4',  pcAsset: 'Quadratec_QR4_SideStep_4D', priceId: 'ss-qr4' },
};

// ── Master lookup ──

export const JEEP_ATTRIBUTE_METADATA: Record<string, Record<string, JeepVariantMetadata>> = {
  'Jeep Type':    JEEP_TYPE,
  'Paint color':  PAINT_COLOR,
  Wheels:         WHEELS,
  Tire:           TIRE,
  'Front Bumper': FRONT_BUMPER,
  'Rear Bumper':  REAR_BUMPER,
  'Front Fender': FRONT_FENDER,
  'Rear Fender':  REAR_FENDER,
  Mirror:         MIRROR,
  'Hood Catcher': HOOD_CATCHER,
  'Light Bar':    LIGHT_BAR,
  'Side Steps':   SIDE_STEPS,
};

/**
 * Look up metadata for a given Jeep attribute + variant name.
 * Returns a sensible auto-generated fallback when the variant is unknown.
 */
export function getJeepVariantMetadata(
  attributeName: string,
  variantName: string
): JeepVariantMetadata {
  const attrMap = JEEP_ATTRIBUTE_METADATA[attributeName];
  if (attrMap?.[variantName]) {
    return attrMap[variantName];
  }

  // Auto-generate a slug-based fallback
  const slug = variantName.toLowerCase().replace(/[\s/]+/g, '-');
  return {
    assetId: slug,
    pcOption: slug,
    pcAsset: null,
  };
}
